// The caption under a page title.
//
// A plain .ts file, not part of the hook, so node --test can import it:
// --experimental-strip-types does not handle .tsx.

/**
 * The parts of the capability set the caption depends on. Structural, so
 * useCapabilities can pass its Capabilities straight in.
 */
export type CaptionInput = {
  canEditFleet: boolean
  canEditDrivers: boolean
  canEditOwners: boolean
  // Added 24 August 2026. Without it Turf and Xolile read "Staff view." on every
  // screen, which tells them nothing about the one thing they can actually do.
  canEditMarketing: boolean
  isAdmin: boolean
  personId: string | null
  status: 'ok' | 'degraded'
}

/**
 * The one-line description of what the signed-in person is looking at.
 *
 * WHY THIS IS HERE AND NOT IN THE ACCESS RESOLVER
 *
 * Until 23 August 2026 every page in the cockpit printed the caption that
 * `interviews/shared/resolveAccess.ts` builds, so Vehicles, Owners, Servicing,
 * Statements and the task board all announced themselves as being about
 * interviews. Correcting the wording there would not have been enough, because
 * that resolver answers a different question: it hard-codes one address as
 * `viewer` for the interviews pipeline, and a viewer there is not read-only in
 * the cockpit. Yolisa is that address. She carries twenty-five open tasks and
 * can edit every one of them, so a role-derived "read only" would have replaced
 * a false noun with a false permission.
 *
 * `cockpit_access` is what actually gates the cockpit, so the caption is
 * derived from the capabilities it returns. The three genuine interview pages
 * (Today, Upcoming interviews, Driver pipeline) still use the resolver's
 * caption, where it is accurate.
 *
 * Nothing is calculated here. The flags are the server's decision; this only
 * chooses words for them.
 */
export function capabilityCaption(capabilities: CaptionInput | null): string {
  if (!capabilities) return ''

  if (capabilities.status === 'degraded') {
    // Never a denial. Say what is uncertain and what to do about it.
    return 'Some permissions could not be read, so you may be seeing less than usual. Reload to try again.'
  }

  if (capabilities.personId === null) {
    // Actionable, and true on every page: anything scoped to a person is empty
    // for a login with no person behind it.
    return 'Your login is not linked to a Fleetly profile, so anything filtered to you will look empty. Ask an admin to link it.'
  }

  if (capabilities.isAdmin) return 'Admin view. You can see and change everything.'

  const editable: string[] = []
  if (capabilities.canEditFleet) editable.push('vehicles')
  if (capabilities.canEditDrivers) editable.push('drivers')
  if (capabilities.canEditOwners) editable.push('owners')
  // "content", not "marketing". The flag governs content plans, content items
  // and their files, and a person told they can edit "marketing" would
  // reasonably expect more than that.
  if (capabilities.canEditMarketing) editable.push('content')

  if (editable.length === 0) return 'Staff view.'

  const list =
    editable.length === 1
      ? editable[0]
      : editable.slice(0, -1).join(', ') + ' and ' + editable[editable.length - 1]

  // Deliberately does not claim the inverse. "You can edit vehicles" is true;
  // "read only otherwise" would not be, because some write paths (the task
  // board) are gated on canView rather than on these flags.
  return `Staff view. You can edit ${list}.`
}
